import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { DollarSign, Users, MousePointer, TrendingUp, Copy, ExternalLink, ArrowLeft } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white/[0.03] border-white/[0.06]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const AffiliateDashboard = () => {
  const { username } = useParams();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);

  useEffect(() => {
    axios.get(`${API}/affiliate/payouts/${username}`)
      .then(res => { setPayouts(res.data.payouts || []); setTotalPaid(res.data.total_paid || 0); })
      .catch(() => {});
  }, [username]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/affiliate/dashboard/${username}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally { setLoading(false); }
    };
    fetch();
  }, [username]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
      <p className="text-gray-400">Loading dashboard...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
      <Card className="bg-white/[0.03] border-white/[0.06] max-w-md">
        <CardContent className="py-12 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/affiliate" className="text-emerald-400 hover:text-emerald-300 text-sm">Join the affiliate program</Link>
        </CardContent>
      </Card>
    </div>
  );

  const aff = data.affiliate;
  const refLink = `${window.location.origin}/ref/${aff.username}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="affiliate-dashboard-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                <span className="text-white font-bold text-xs">MD</span>
              </div>
              <span className="text-white font-semibold">Miadesk</span>
            </Link>
            <div className="h-5 w-px bg-white/10" />
            <span className="text-emerald-400 text-sm font-medium">Affiliate Dashboard</span>
          </div>
          <Link to="/affiliate" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="affiliate-welcome">Welcome, {aff.name}</h1>
          <p className="text-gray-500 text-sm">Track your referrals, earnings, and performance</p>
        </div>

        {/* Referral Link */}
        <Card className="bg-emerald-600/5 border-emerald-500/20 mb-8" data-testid="referral-link-card">
          <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">Your Referral Link</p>
              <p className="text-lg font-mono text-emerald-400 font-bold" data-testid="referral-link-display">{refLink}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigator.clipboard.writeText(refLink).then(() => toast({ title: 'Link copied!' }))} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-referral-link">
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={MousePointer} label="Total Clicks" value={aff.total_clicks || 0} color="bg-blue-600" />
          <StatCard icon={Users} label="Referrals" value={aff.total_referrals || 0} color="bg-emerald-600" />
          <StatCard icon={TrendingUp} label="Conversion" value={`${data.conversion_rate || 0}%`} color="bg-amber-600" />
          <StatCard icon={DollarSign} label="Earnings" value={`$${(aff.total_earnings || 0).toFixed(2)}`} color="bg-purple-600" />
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Account Info */}
          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader><CardTitle className="text-white text-base">Account Info</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-gray-500 text-sm">Commission Rate</span>
                <span className="text-emerald-400 font-medium text-sm">{aff.commission_rate}%</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-gray-500 text-sm">Status</span>
                <span className={`text-sm font-medium ${aff.is_active ? 'text-emerald-400' : 'text-red-400'}`}>{aff.is_active ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-gray-500 text-sm">Joined</span>
                <span className="text-gray-300 text-sm">{new Date(aff.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500 text-sm">Clicks (This Month)</span>
                <span className="text-gray-300 text-sm">{data.clicks_30d || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals */}
          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader><CardTitle className="text-white text-base">Recent Referrals</CardTitle></CardHeader>
            <CardContent>
              {data.referrals?.length > 0 ? (
                <div className="space-y-3">
                  {data.referrals.map((r, i) => (
                    <div key={r.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white text-sm">{r.user_email || 'Anonymous'}</p>
                        <p className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.subscribed ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}>
                        {r.subscribed ? 'Subscribed' : 'Free'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-6">No referrals yet. Share your link to start earning!</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payout History */}
        <Card className="bg-white/[0.03] border-white/[0.06] mt-6" data-testid="payout-history-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-base">Payout History</CardTitle>
            <span className="text-xs text-gray-500">Paid out to date: <span className="text-emerald-400 font-medium">${totalPaid.toFixed(2)}</span></span>
          </CardHeader>
          <CardContent>
            {payouts.length > 0 ? (
              <div className="space-y-2">
                {payouts.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`payout-${p.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">${(p.amount || 0).toFixed(2)}</p>
                        <p className="text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      p.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                      p.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                    }`}>{p.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-6" data-testid="payout-history-empty">No payouts yet. Payouts run automatically every 2 weeks once you have earnings.</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AffiliateDashboard;
